import { Clock } from "lucide-react";

const DAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

// Convert "HH:MM" → "h:MM AM/PM"
function formatTime(t) {
  if (!t || typeof t !== "string") return "";
  const [hStr, mStr] = t.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (isNaN(h) || isNaN(m)) return t;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m.toString().padStart(2, "0")} ${ampm}`;
}

function hasAnyHours(hours) {
  if (!hours || typeof hours !== "object") return false;
  return DAYS.some(({ key }) => {
    const e = hours[key];
    return e && (e.closed || (e.open && e.close));
  });
}

// Mon=1 ... Sun=7 in JS Date.getDay() is Sun=0 ... Sat=6. Map to our keys.
const JS_DAY_TO_KEY = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

function isOpenNow(hours) {
  if (!hours) return { open: false, label: "" };
  const now = new Date();
  const todayKey = JS_DAY_TO_KEY[now.getDay()];
  const todayEntry = hours[todayKey];
  if (!todayEntry || todayEntry.closed || !todayEntry.open || !todayEntry.close) {
    return { open: false, label: "Closed today" };
  }
  const [oh, om] = todayEntry.open.split(":").map(Number);
  const [ch, cm] = todayEntry.close.split(":").map(Number);
  if ([oh, om, ch, cm].some(isNaN)) return { open: false, label: "" };
  const cur = now.getHours() * 60 + now.getMinutes();
  const opn = oh * 60 + om;
  let cls = ch * 60 + cm;
  // Handle overnight (e.g. open 18:00 close 02:00) — treat close < open as next-day close
  if (cls <= opn) cls += 24 * 60;
  const curAdj = cur < opn ? cur + 24 * 60 : cur;
  if (curAdj >= opn && curAdj < cls) {
    return { open: true, label: `Open until ${formatTime(todayEntry.close)}` };
  }
  return { open: false, label: `Closed · Opens ${formatTime(todayEntry.open)}` };
}

export default function HoursDisplay({ hours }) {
  if (!hasAnyHours(hours)) return null;
  const status = isOpenNow(hours);
  const todayKey = JS_DAY_TO_KEY[new Date().getDay()];

  return (
    <div className="mt-8" data-testid="business-hours-block">
      <h3 className="overline text-gray-500 inline-flex items-center gap-1.5">
        <Clock size={12} /> Hours of operation
      </h3>
      {status.label && (
        <div className="mt-3">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${
              status.open
                ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                : "bg-gray-100 text-gray-700 border-gray-300"
            }`}
            data-testid="business-hours-open-badge"
          >
            <span
              className={`h-2 w-2 rounded-full ${status.open ? "bg-emerald-500" : "bg-gray-400"}`}
              aria-hidden="true"
            />
            {status.label}
          </span>
        </div>
      )}
      <dl className="mt-4 max-w-md border border-border rounded-md divide-y bg-white">
        {DAYS.map(({ key, label }) => {
          const entry = hours[key];
          const isToday = key === todayKey;
          let valueText = "Closed";
          let valueClass = "text-gray-500";
          if (entry && !entry.closed && entry.open && entry.close) {
            valueText = `${formatTime(entry.open)} – ${formatTime(entry.close)}`;
            valueClass = "text-gray-900";
          }
          return (
            <div
              key={key}
              className={`flex items-center justify-between px-3 py-2 text-sm ${
                isToday ? "bg-[#B31E1E]/5" : ""
              }`}
              data-testid={`business-hours-row-${key}`}
            >
              <dt className={`font-medium ${isToday ? "text-[#B31E1E]" : "text-gray-700"}`}>
                {label}
                {isToday && (
                  <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-[#B31E1E]">
                    Today
                  </span>
                )}
              </dt>
              <dd className={`tabular-nums font-mono text-xs ${valueClass}`}>{valueText}</dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}
